import { useEffect, useRef, useState } from 'react';
import type { McpNodeData, McpNodeFormState } from '../../../../types/mcpNode';
import { initialMcpFormState } from '../../../../types/mcpNode';

const MCP_FORM_SYNC_KEYS: (keyof McpNodeFormState)[] = [
  'serverUrl',
  'toolName',
  'prompt',
  'authToken',
  'integrationId',
  'text',
];

export function useMcpNodeState(data: McpNodeData) {
  const [formData, setFormData] = useState<McpNodeFormState>(() =>
    initialMcpFormState(data),
  );
  const userEditedFieldsRef = useRef<Set<string>>(new Set());
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    const fromData: Partial<McpNodeFormState> = {};
    const d = dataRef.current;
    if (!d) return;
    MCP_FORM_SYNC_KEYS.forEach(k => {
      if (d[k as string] === undefined) return;
      if (userEditedFieldsRef.current.has(k as string)) return;
      const dataArray = d[`${String(k)}Data`];
      const hasDataFromEdge =
        Array.isArray(dataArray) && dataArray.length >= 1;
      if (!hasDataFromEdge) return;
      const v = d[k as string];
      if (typeof v === 'string') {
        (fromData as Record<string, string>)[k as string] = v;
      }
    });
    if (Object.keys(fromData).length === 0) return;
    setFormData(prev => {
      const hasChange = (Object.keys(fromData) as (keyof McpNodeFormState)[]).some(
        key => prev[key] !== fromData[key],
      );
      if (!hasChange) return prev;
      return { ...prev, ...fromData };
    });
  }, [data]);

  const markUserEdited = (fieldName: string) => {
    userEditedFieldsRef.current.add(fieldName);
  };

  return {
    formData,
    setFormData,
    userEditedFieldsRef,
    markUserEdited,
  };
}
